import React, { type ReactNode, useContext, useMemo } from 'react';

const environments = ['localProd', 'localDev', 'dev', 'prod'] as const;

export type Environment = (typeof environments)[number];

const ctx = React.createContext<Environment>('prod');

export const isEnvironment = (value: unknown): value is Environment =>
  new Set<unknown>(environments).has(value);

export interface EnvironmentConfig {
  auth: {
    clientId: string;
    domain: string;
    internalTestPartnerUuid: string;
    internalTestPartnerOrganizationId: string;
    redirectUri: string;
    namespace: string;
  };
  baseUrls: {
    backoffice: string;
    graphql: string;
    noti: string;
    users: string;
    panels: string;
    observability: string;
    demi: string;
    search: string;
  };
  seekAnzSchemeId: 'seekAnz' | 'seekAnzDev' | 'seekAnzPublicTest';
  siteKey: string;
  enableTurnstile?: boolean;
  trackingEnabled: boolean;
}

const environmentConfigs: Record<Environment, () => EnvironmentConfig> = {
  localDev: () => ({
    ...environmentConfigs.dev(),
    auth: {
      ...environmentConfigs.dev().auth,
      redirectUri: 'https://dev.seek.com:12915/authorize',
    },
    baseUrls: {
      ...environmentConfigs.dev().baseUrls,
      // Uncomment this to run against local Partner Noti Config.
      // noti: 'http://localhost:60959',
      // Uncomment this to run against local Dua Lipa.
      // users: 'http://localhost:11032',
      // Uncomment this to run against local indie-moat
      // observability: 'http://localhost:35116/observability',
      // TODO: Should we support an option to use staging/production search for a simpler local setup?
      search: '',
    },
    enableTurnstile: false,
    trackingEnabled: false,
  }),
  localProd: () => ({
    ...environmentConfigs.prod(),
    auth: {
      ...environmentConfigs.prod().auth,
      redirectUri: 'https://dev.seek.com:12915/authorize',
    },
    baseUrls: {
      ...environmentConfigs.prod().baseUrls,
      // Uncomment this to run against local Partner Noti Config.
      // noti: 'http://localhost:60959',
      // Uncomment this to run against local Dua Lipa.
      // users: 'http://localhost:11032',
      // Uncomment this to run against local indie-moat
      // observability: 'http://localhost:35116/observability',
      // TODO: Should we support an option to use staging/production search for a simpler local setup?
      search: '',
    },
    enableTurnstile: false,
    trackingEnabled: false,
  }),
  dev: () => ({
    auth: {
      clientId: 'itjwZAy0tqBENXrHjrLRzvNf1bocz7NI',
      domain: 'auth.staging.seek.com',
      // https://ui.backoffice.dev.myseek.xyz/organization?seekWebId=48003533
      internalTestPartnerUuid: '7ccfc48a-7d48-4a40-bdcf-fb34f8dd87f9',
      internalTestPartnerOrganizationId: 'org_C5DkGCakMdouRsrF',
      redirectUri: 'https://developer.staging.seek.com/authorize',
      namespace: 'https://dev.graphql.seek.com',
    },
    baseUrls: {
      backoffice: 'https://ui.backoffice.dev.myseek.xyz',
      graphql: 'https://graphql.staging.seek.com',
      noti: 'https://seek-api-notifications.staging.cloud.seek.com.au',
      users: 'https://seek-api-users.staging.cloud.seek.com.au',
      panels: 'https://integration.staging.seek.com/panels',
      observability:
        'https://seek-api-observability.staging.cloud.seek.com.au/observability',
      demi: 'https://developer.staging.seek.com',
      search: 'https://developer-site-search.staging.cloud.seek.com.au',
    },
    seekAnzSchemeId: 'seekAnzDev',
    siteKey: '0x4AAAAAAA0miEK9hSe80j1Q',
    enableTurnstile: true,
    trackingEnabled: false,
  }),
  prod: () => ({
    auth: {
      clientId: 'etgCFP9H3b10Mf9OW8vPO8bCFwJaHgdu',
      domain: 'auth.seek.com',
      // https://ui.backoffice.prod.myseek.xyz/organization?seekWebId=44739251
      internalTestPartnerUuid: 'f5c50ad6-0f8f-1eea-9782-42e91d500980',
      internalTestPartnerOrganizationId: 'org_JjTLJBRISXdWYQIi',
      redirectUri: 'https://developer.seek.com/authorize',
      namespace: 'https://graphql.seek.com',
    },
    baseUrls: {
      backoffice: 'https://ui.backoffice.prod.myseek.xyz',
      graphql: 'https://graphql.seek.com',
      noti: 'https://seek-api-notifications.cloud.seek.com.au',
      users: 'https://seek-api-users.cloud.seek.com.au',
      panels: 'https://integration.seek.com/panels',
      observability:
        'https://seek-api-observability.cloud.seek.com.au/observability',
      demi: 'https://developer.seek.com',
      search: 'https://developer-site-search.cloud.seek.com.au',
    },
    seekAnzSchemeId: 'seekAnz',
    siteKey: '0x4AAAAAAA0mheFaHUvKepTU',
    enableTurnstile: true,
    trackingEnabled: true,
  }),
};

interface EnvironmentProviderProps {
  children: ReactNode;
  value: Environment;
}

export const EnvironmentProvider = ({
  children,
  value,
}: EnvironmentProviderProps) => (
  <ctx.Provider value={value}>{children}</ctx.Provider>
);

export const useEnvironmentConfig = () => {
  const environment = useContext(ctx);
  return useMemo(() => environmentConfigs[environment](), [environment]);
};

export const useEnvironment = () => useContext(ctx);
